export const ptimHero = {
  badge: "Test-Before-Scale Programme",
  title: "Practically Tested Innovation Model",
  subtitle:
    "PTIM bridges the gap between a promising PIIC outcome and a factory-wide rollout. It creates a structured, time-bound test environment where an innovation is validated under real production conditions before capital and labour are committed at scale.",
  ctaPrimary: "Start a PTIM Pilot",
  ctaSecondary: "Explore the Process",
};

export const ptimWhatIs = {
  title: "What is PTIM?",
  paragraphs: [
    "PTIM - the Practically Tested Innovation Model - is TIE's structured validation programme that protects factories from the risk of scaling unproven solutions. After PIIC generates promising innovations, PTIM steps in to test those solutions under live production conditions before any major investment is made.",
    "Unlike traditional pilot programmes that run informally, PTIM applies a rigorous experimental framework. It defines success criteria upfront, controls for variables, measures cause-and-effect relationships, and produces a documented go / no-go recommendation backed by data.",
    "PTIM is where innovation becomes investment-ready. Factory leadership receives a validated ROI projection, a risk profile, and a standard operating procedure - all tested on the actual machines, with the actual team, under actual production pressure.",
  ],
  highlights: [
    { label: "Duration", value: "2-3 Months" },
    { label: "Test Type", value: "Controlled" },
    { label: "Output", value: "Go / No-Go" },
    { label: "Risk Reduction", value: "Evidence-Based" },
  ],
};

export const ptimProcessSteps = [
  {
    phase: "Phase 1",
    week: "Week 1",
    title: "Scope & Success Criteria",
    description:
      "The PIIC-generated innovation is reviewed and formalized into a testable hypothesis. Success metrics, control parameters, and resource requirements are defined and agreed with factory leadership.",
    activities: [
      "Innovation handover from PIIC circles",
      "Hypothesis definition & variable mapping",
      "Success criteria & KPI benchmarking",
      "Resource & timeline confirmation",
    ],
  },
  {
    phase: "Phase 2",
    week: "Weeks 2-5",
    title: "Controlled Testing",
    description:
      "The innovation is deployed in a controlled production environment - typically one line, one shift, or one process. Data is collected daily against the baseline. Adjustments are made in real time based on performance feedback.",
    activities: [
      "Controlled line / shift deployment",
      "Daily metric collection & logging",
      "Real-time adjustment & iteration",
      "Deviation tracking & root-cause response",
    ],
  },
  {
    phase: "Phase 3",
    week: "Weeks 6-8",
    title: "Validation & Analysis",
    description:
      "Test results are analyzed against the success criteria. Statistical significance is assessed. Financial impact is calculated. A comprehensive validation report is prepared with clear recommendations.",
    activities: [
      "Statistical validation of results",
      "ROI & risk profile calculation",
      "Comparative before-and-after analysis",
      "Validation report drafting",
    ],
  },
  {
    phase: "Phase 4",
    week: "Weeks 9-10",
    title: "SOP & Scale Decision",
    description:
      "If the innovation passes validation, a standard operating procedure is drafted for factory-wide rollout. If it does not pass, the findings are documented and the factory avoids a costly misinvestment.",
    activities: [
      "Go / no-go recommendation presentation",
      "SOP drafting for approved innovations",
      "Scale-up resource & timeline planning",
      "Knowledge capture & repository update",
    ],
  },
];

export const ptimWhyItMatters = [
  {
    title: "Prevents Costly Rollout Failures",
    description:
      "Many factories invest heavily in solutions that worked in a lab or another facility but fail in their specific context. PTIM catches these mismatches before capital is committed.",
    icon: "ri-shield-check-line",
  },
  {
    title: "Builds Management Confidence",
    description:
      "When leadership sees verified data from their own production floor, approval for scale-up becomes a data-driven decision rather than a leap of faith.",
    icon: "ri-bar-chart-grouped-line",
  },
  {
    title: "Creates Repeatable Protocols",
    description:
      "Every PTIM produces an SOP and a documented method. This means the same innovation can be rolled out across multiple factory units with predictable results.",
    icon: "ri-loop-left-line",
  },
  {
    title: "Protects Worker Productivity",
    description:
      "Testing on a single line first means operators are not disrupted across the entire factory. Changes are introduced gradually, with training and feedback loops built in.",
    icon: "ri-team-line",
  },
  {
    title: "Quantifies Risk Accurately",
    description:
      "PTIM produces a risk matrix with probability-weighted outcomes. Management knows the upside, the downside, and the probability of each before making a decision.",
    icon: "ri-calculator-line",
  },
  {
    title: "Feeds the Knowledge Repository",
    description:
      "Every PTIM result - whether go or no-go - is documented and added to TIE's shared knowledge base, benefiting the entire ecosystem.",
    icon: "ri-database-2-line",
  },
];

export const ptimOutcomes = {
  title: "What Every PTIM Produces",
  items: [
    {
      title: "Validated ROI & Risk Profile",
      description:
        "A quantitative financial assessment showing projected return, payback period, and probability-weighted risk scenarios based on real production data.",
      icon: "ri-line-chart-line",
    },
    {
      title: "Standard Operating Procedure",
      description:
        "A detailed SOP for rolling the validated innovation across factory lines, including step-by-step instructions, training requirements, and quality checkpoints.",
      icon: "ri-book-open-line",
    },
    {
      title: "Go / No-Go Decision Framework",
      description:
        "A clear recommendation with supporting evidence, enabling factory leadership to make an informed capital allocation decision in under 30 minutes.",
      icon: "ri-git-merge-line",
    },
    {
      title: "Before & After Evidence Pack",
      description:
        "Comprehensive documentation with metrics, photographs, process maps, and operator feedback capturing the complete transformation story.",
      icon: "ri-folder-chart-line",
    },
    {
      title: "Line-Ready Implementation Plan",
      description:
        "A practical rollout plan with timelines, resource requirements, training schedules, and governance checkpoints for scaling across operations.",
      icon: "ri-map-pin-time-line",
    },
    {
      title: "Knowledge Transfer Session",
      description:
        "A structured handover session where the PTIM team trains internal staff to replicate, monitor, and sustain the validated innovation independently.",
      icon: "ri-presentation-line",
    },
  ],
};

export const ptimStats = [
  { value: "250+", label: "Projects Deployed", sub: "across 80+ factories" },
  { value: "2-3", label: "Months", sub: "average PTIM duration" },
  { value: "100%", label: "Data-Driven", sub: "every decision backed by evidence" },
  { value: "94%", label: "Go Rate", sub: "innovations that pass validation" },
];